import axios, {
  AxiosError,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { formatStructuredApiErrorResponse } from '../utils/errorUtils';
import { apiConfig } from './apiConfig';
import { getGlobalErrorHandler } from './errorHandler';
import { setBackendAvailability } from './backendStatus';

export interface AuthAxiosRequestConfig extends AxiosRequestConfig {
  requiresAuth?: boolean;
}

const shouldLogHttpDebug = (): boolean => {
  if (process.env.NODE_ENV === 'production') {
    return false;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  return (
    window.location.search.includes('httpDebug=true') ||
    window.localStorage.getItem('http-debug-mode') === 'true'
  );
};

const logRequestDebug = (config: InternalAxiosRequestConfig): void => {
  if (!shouldLogHttpDebug()) {
    return;
  }

  const headers = { ...(config.headers || {}) } as Record<string, string>;
  if (headers.Authorization) {
    headers.Authorization = 'Bearer [redacted]';
  }

  console.debug('[HTTP DEBUG] Outgoing request', {
    method: config.method?.toUpperCase(),
    url: config.url,
    baseURL: config.baseURL,
    withCredentials: config.withCredentials,
    headers,
  });
};

export const axiosClient = axios.create({
  baseURL: apiConfig.baseURL,
  withCredentials: false,
});

export const publicAxiosClient = axios.create({
  baseURL: apiConfig.baseURL,
  withCredentials: false,
});

const handleResponseError = async (error: AxiosError) => {
  let errorMessage = 'An unknown error occurred';
  let errorTitle = 'Error';
  let severity: 'error' | 'warning' | 'info' = 'error';

  if (error.response) {
    try {
      if (error.response.data && typeof error.response.data === 'object') {
        const structured = formatStructuredApiErrorResponse(error.response.data);
        if (structured && structured !== 'Unknown error') {
          errorMessage = structured;
        }
      }

      if (errorMessage === 'An unknown error occurred' && error.response.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if ((error.response.data as any).message) {
          errorMessage = (error.response.data as any).message;
        } else if ((error.response.data as any).error?.message) {
          errorMessage = (error.response.data as any).error.message;
        } else if ((error.response.data as any).error) {
          errorMessage =
            typeof (error.response.data as any).error === 'string'
              ? (error.response.data as any).error
              : JSON.stringify((error.response.data as any).error);
        }
      }
    } catch (e) {
      // Unable to parse error response data
    }

    if (error.response.status === 401) {
      errorTitle = 'Authentication Required';
      errorMessage = 'Your session has expired. Please log in again.';

      (async () => {
        try {
          await signOut();
        } catch (signOutError) {
          console.warn('Error signing out:', signOutError);
        }
      })();

      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    }

    if (error.response.status === 403) {
      errorTitle = 'Permission Denied';
      errorMessage = 'You do not have permission to perform this action.';
    }

    if (error.response.status === 404) {
      errorTitle = 'Resource Not Found';
      errorMessage = 'The requested resource could not be found.';
      severity = 'warning';
    }

    if (error.response.status === 422) {
      errorTitle = 'Validation Error';
      if ((error.response.data as any)?.errors) {
        const errorFields = Object.keys((error.response.data as any).errors);
        if (errorFields.length > 0) {
          errorMessage = `Validation failed: ${errorFields.join(', ')}`;
        }
      }
    }

    if (error.response.status >= 500) {
      errorTitle = 'Server Error';
      errorMessage = 'The server encountered an error. Please try again later.';
      setBackendAvailability(false);
    }
  } else if ((error as any).request) {
    setBackendAvailability(false);
    errorTitle = 'Connection Error';
    errorMessage =
      'Unable to connect to the server. Please check your internet connection.';
  }

  const globalErrorHandler = getGlobalErrorHandler();
  if (globalErrorHandler) {
    globalErrorHandler({
      title: errorTitle,
      message: errorMessage,
      severity,
      autoHide: severity !== 'error',
    });
  }

  return Promise.reject(error);
};

axiosClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig & { requiresAuth?: boolean }) => {
    if (config.requiresAuth === false) {
      logRequestDebug(config);
      return config;
    }

    try {
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();

      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      } else {
        return Promise.reject(
          new Error('Authentication required: access token unavailable')
        );
      }
    } catch (error) {
      return Promise.reject(
        new Error('Authentication required: failed to fetch Amplify session')
      );
    }

    logRequestDebug(config);
    return config;
  },
  error => {
    console.error('Error in request interceptor:', error);
    return Promise.reject(error);
  }
);

axiosClient.interceptors.response.use(
  response => {
    setBackendAvailability(true);
    return response;
  },
  handleResponseError
);

publicAxiosClient.interceptors.request.use(config => {
  logRequestDebug(config);
  return config;
});

publicAxiosClient.interceptors.response.use(
  response => {
    setBackendAvailability(true);
    return response;
  },
  handleResponseError
);

export default axiosClient;
